import React from "react";
import {useSetStyleRegistory} from "../../hooks/useSetStyleRegistory";
import {CellContent} from "../../models/CellContent";
import {ContentCell} from "../../models/ContentCell";
import {ContentKeyPair} from "../../models/Keys";
import {Rect} from "../../models/Rect";
import {ResizableBox} from "./ResizableBox";
import {KeyboardEventUtil} from "../../utils/KeyboardEventUtil";
import {MIN_CELL_HEIGHT, MIN_CELL_WIDTH} from "../../models/constants";
import {CJMapManager} from "../../models/CJmapManager";
import {PerceptionEditor} from "./CellEditor/PerceptionEditor";
import {BehaviorEditor} from "./CellEditor/BehaviorEditor";
import {debug_log} from "../../utils/debug";
import {fireCopyEvent, selectElement} from "../../utils/browser";
import {ClipboardCell} from "../../models/ClipboardCell";

type RCContentCellProps = {
    cell: ContentCell;
    manager: CJMapManager;
    readOnly: boolean;
    onSelectCell: (cell_key: ContentKeyPair) => void;
    onUnSelectCell: (cell_key: ContentKeyPair) => void;
    onEditStart: () => void;
    onChangeContent: (cell_key: ContentKeyPair, content: CellContent) => void;
    onEditEndWithChanged: (cell_key: ContentKeyPair, content: CellContent, height: number | null, before: CJMapManager) => void;
    onEditEndNoChanged: () => void;
    onResize: (cell_key: ContentKeyPair, rect: Rect) => void;
    onResizeTemporary: (cell_key: ContentKeyPair, height: number) => boolean;
}
export const RCContentCell = (props: RCContentCellProps) => {

    const ref = React.useRef<HTMLDivElement>(null);
    const [before_cell_content, setBeforeCellContent] = React.useState<CellContent | null>(null);
    const cell_state = props.cell.State;

    const [growthedheight, setGrowthedHeight] = React.useState<number | null>(null);
    const [before_manager, setBeforeManager] = React.useState<CJMapManager>(props.manager);

    const [show_collaborator_label, setShowCollaboratorLabel] = React.useState(false);

    const handleStartEdit = () => {
        if (props.readOnly) {
            return;
        }

        setGrowthedHeight(null);
        setBeforeManager(props.manager);
        setBeforeCellContent(props.cell.data);
        props.onEditStart();
    }

    const handleChangeContent = (content: CellContent) => {
        props.onChangeContent(props.cell.KeyPair, content);
    }

    const handleEditorBlur = (content: CellContent) => {
        if (before_cell_content?.equals(content) === false) {
            debug_log("handleEditorBlur(changed)", before_cell_content, props.cell.data);
            props.onEditEndWithChanged(props.cell.KeyPair, content, growthedheight, before_manager);
        } else {
            debug_log("handleEditorBlur(no changed)", before_cell_content, props.cell.data);
            props.onEditEndNoChanged();
        }
    }

    const setStyleRegistry = useSetStyleRegistory();
    React.useEffect(() => {
        setStyleRegistry.add(props.cell.ContentKey, ref);
        setStyleRegistry.add(props.cell.FunnelKey, ref);
        return () => {
            setStyleRegistry.remove(props.cell.ContentKey, ref);
            setStyleRegistry.remove(props.cell.FunnelKey, ref);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        //debug_log("RCContentCell.handleClick:" , cell_state.IsSelected);
        e.stopPropagation();

        if (cell_state.IsSelected) {
            if (!cell_state.IsEdit) {
                handleStartEdit();
            }
        } else {
            props.onSelectCell(props.cell.KeyPair);
        }
    }

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (KeyboardEventUtil.isStartEditKey(e.nativeEvent)) {
            handleStartEdit();
            e.preventDefault();
        }

        if (KeyboardEventUtil.isCopyKey(e.nativeEvent)) {
            if (ref.current) {
                // focus() により Keyboard Event は受取れるようになるが、その状態で Ctrl+C しても Copy Event が発火しない
                // そのため、Selection に DOM を追加して execCommand を呼ぶことで、強制的に Copy Event を発火させる
                fireCopyEvent(ref.current);
                e.preventDefault();
            }
        }

        if (KeyboardEventUtil.isPasteKey(e.nativeEvent)) {
            if (ref.current) {
                // selection を、focus のある Element に設定することで、 Paste Event が発火する
                // なお、ここで e.preventDefault() してしまうと  Paste Event は発火しない
                // (execCommand を呼び出しても Paste Event は発火しなかった)
                selectElement(ref.current);
            }
        }
    }

    const handleResize = (rect: Rect, _: Rect) => {
        props.onResize(props.cell.KeyPair, rect);
    }
    const handleChangeEditorHeight = (height: number) => {
        const cell_height = height + (10 * 2)/* .data_cell's padding */;

        const growthed = props.onResizeTemporary(props.cell.KeyPair, cell_height);
        if (growthed) {
            setGrowthedHeight(cell_height);
        }
    }

    const handleCopy: React.ClipboardEventHandler = (e) => {
        e.clipboardData.setData('text/plain', props.cell.data.toText());
        e.clipboardData.setData('application/x-journey-cycle-editor', props.cell.toClipboardData().toString());
        e.preventDefault();
    }

    const handlePaste: React.ClipboardEventHandler = (e) => {
        if (props.readOnly) {
            return;
        }

        debug_log("handlePaste");
        const app_data = e.clipboardData.getData('application/x-journey-cycle-editor');
        if (app_data) {
            const clipboard = ClipboardCell.fromString(app_data);
            if (clipboard.isPasteableTo(props.cell)) {
                const height = Math.max(props.cell.State.Height, clipboard.Size.Height);
                props.onEditEndWithChanged(props.cell.KeyPair, clipboard.Content, height, props.manager);
            }
        }

        e.preventDefault();
    }


    React.useEffect(() => {
        if (props.cell.State.IsSelected) {
            ref.current?.focus();
        }
    }, [props.cell.State.IsSelected]);

    if (cell_state.HiddenBy !== 'none') {
        const style = (() => {
            if (cell_state.HiddenBy === 'row') {
                return {height: 5};
            } else if (cell_state.HiddenBy === 'column') {
                return {width: 5};
            } else {
                return {width: 5, height: 5};
            }
        })();
        return <div className="data_cell_hidden" style={style}/>;
    } else {
        const class_names = ['data_cell'];
        if (cell_state.IsEdit || cell_state.IsSelected) {
            class_names.push(cell_state.IsEdit ? 'is_edit': 'is_selected');
        }
        if (0 < cell_state.Collaborators.length){
            class_names.push('is_collaborator_selected');
        }

        return (
            <div ref={ref}
                 className={class_names.join(' ')}
                 onMouseEnter={(e) => setShowCollaboratorLabel(true)}
                 onMouseLeave={(e) => setShowCollaboratorLabel(false)}
                 onClick={handleClick}
                 onKeyDown={handleKeyDown}
                 onCopy={handleCopy}
                 onPaste={handlePaste}
                 tabIndex={0} /* hack for capture keyboard event */
                 >
                <div className="editor_area"
                     style={{overflow: cell_state.IsSelected || cell_state.IsEdit ? 'visible' : 'hidden'}}>
                    {props.cell.State.EditorType === 'perception'
                        ? <PerceptionEditor content={props.cell.data} onBlur={handleEditorBlur}
                                            isEdit={cell_state.IsEdit}
                                            onChangeEditorHeight={handleChangeEditorHeight}
                                            onChangeContent={handleChangeContent}
                        />
                        : <BehaviorEditor content={props.cell.data} onBlur={handleEditorBlur}
                                          isSelected={cell_state.IsSelected}
                                          isEdit={cell_state.IsEdit}
                                          readOnly={props.readOnly}
                                          editorType={props.cell.State.EditorType}
                                          cellWidth={cell_state.Width}
                                          lineBackgroundColor={props.cell.State.ActionLineColor}
                                          onChangeEditorHeight={handleChangeEditorHeight}
                                          onChangeContent={handleChangeContent}
                        />
                    }
                </div>
                {cell_state.IsSelected && !cell_state.IsEdit && !props.readOnly
                    && <ResizableBox elementRef={ref}
                                     cover={false}
                                     minWidth={MIN_CELL_WIDTH}
                                     minHeight={MIN_CELL_HEIGHT}
                                     onResize={handleResize}/>
                }
                {0 < cell_state.Collaborators.length &&
                  <div className="collaborator_label" style={{display: show_collaborator_label ? 'block': 'none'}}>
                      {cell_state.Collaborators.join(", ")}
                  </div>
                }
            </div>
        )
    }
}
